/**
 * ConnectionLines — renders connection and parent-child edges between nodes.
 *
 * Dark-themed: subtle blue-gray lines that glow on hover/selection.
 * Highlighted edges (from active node) are clickable for selection.
 * Selected edges show a distinct visual indicator and can be deleted.
 *
 * WQ-343: highlighted lines keep their own colour and style (D2a); a line's
 * label is drawn when it is highlighted or selected, and for every labelled
 * line once zoomed in to k ≥ 1.5 (D3a).
 */

import { useMemo } from 'react';
import type { AdjacencyNode, ConnectionEdge, RelationshipType } from '../../types/adjacency';
import type { Position } from '../../utils/positioning';
import type { SelectedEdge } from '../../store/ui-slice';
import { connectionKey } from '../../utils/connections';

// ── Types ────────────────────────────────────────────────────────────

interface ConnectionLinesProps {
  nodes: AdjacencyNode[];
  positions: Map<string, Position>;
  hoveredNodeId: string | null;
  selectedNodeId: string | null;
  selectedEdge: SelectedEdge | null;
  filterPassingIds: Set<string>;
  relationshipFilter: RelationshipType[];
  /** Current zoom scale (d3 transform k) — labels show for all lines at k ≥ LABEL_ZOOM. */
  zoomScale?: number;
  onEdgeClick: (edge: SelectedEdge) => void;
  onEdgeDoubleClick: (edge: SelectedEdge) => void;
}

export interface Edge {
  key: string;
  fromId: string;
  toId: string;
  type: 'peer' | 'parent-child';
  lineStyle: 'solid' | 'dashed' | 'dotted';
  lineColor: string;
  label: string | null;
}

// ── Constants ────────────────────────────────────────────────────────

const ARC_FACTOR = 0.15;

/** Dark theme line styles. */
const DIM_COLOR = '#64748b';
const DIM_OPACITY = 0.40;
const DIM_STROKE_WIDTH = 1.5;

const HIGHLIGHT_OPACITY = 1;
const HIGHLIGHT_STROKE_WIDTH = 2.5;

/** Relationship defaults (match relationship-slice) — used when a row has no colour. */
const DEFAULT_LINE_COLORS: Record<Edge['type'], string> = {
  'parent-child': '#3b82f6',
  'peer': '#f59e0b',
};

/** Labels for every labelled line from this zoom level (D3a). */
export const LABEL_ZOOM = 1.5;
const LABEL_FONT_SIZE = 9;
const LABEL_MAX_CHARS = 40;

/** Selected edge visual — bright cyan for clear distinction. */
const SELECTED_EDGE_COLOR = '#38bdf8';
const SELECTED_EDGE_WIDTH = 3.5;
const SELECTED_EDGE_OPACITY = 0.95;

const GHOST_OPACITY = 0.05;

/** Invisible hit area width for clicking lines. */
const HIT_AREA_WIDTH = 16;

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Is this row the one whose style represents its mirrored pair (F2b)? The row
 * stored on the parent for parent-child, the lexically smaller node id for
 * peers — so a pair whose two rows disagree (legacy data) looks the same
 * however the nodes are ordered. Display only; nothing is rewritten.
 */
function isCanonicalRow(nodeId: string, edge: ConnectionEdge): boolean {
  if (edge.relationship === 'peer') return nodeId < edge.targetId;
  return edge.relationship === 'parent';
}

/**
 * One Edge per connection. Geometry (from/to) comes from the first row seen,
 * exactly as before; style and label come from the canonical row when present.
 */
export function buildEdges(nodes: AdjacencyNode[]): Edge[] {
  const byKey = new Map<string, Edge>();
  for (const node of nodes) {
    for (const edge of node.edges) {
      const isPC = edge.relationship === 'parent' || edge.relationship === 'child';
      const key = connectionKey(node.id, edge);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          key,
          fromId: isPC && edge.relationship === 'child' ? edge.targetId : node.id,
          toId: isPC && edge.relationship === 'child' ? node.id : edge.targetId,
          type: isPC ? 'parent-child' : 'peer',
          lineStyle: edge.lineStyle,
          lineColor: edge.lineColor,
          label: edge.label || null,
        });
      } else if (isCanonicalRow(node.id, edge)) {
        existing.lineStyle = edge.lineStyle;
        existing.lineColor = edge.lineColor;
        existing.label = edge.label || null;
      }
    }
  }
  return [...byKey.values()];
}

/** Midpoint of the quadratic curve: 0.25·from + 0.5·control + 0.25·to. */
export function curveMidpoint(
  from: Position, to: Position, cx: number, cy: number,
): { x: number; y: number } {
  return {
    x: 0.25 * from.x + 0.5 * cx + 0.25 * to.x,
    y: 0.25 * from.y + 0.5 * cy + 0.25 * to.y,
  };
}

function controlPoint(
  x1: number, y1: number,
  x2: number, y2: number,
): { cx: number; cy: number } {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return { cx: mx, cy: my };

  const offset = dist * ARC_FACTOR;
  const cx = mx + (-dy / dist) * offset;
  const cy = my + (dx / dist) * offset;
  return { cx, cy };
}

function isEdgeSelected(edge: Edge, sel: SelectedEdge | null): boolean {
  if (!sel) return false;
  return (
    (edge.fromId === sel.sourceId && edge.toId === sel.targetId) ||
    (edge.fromId === sel.targetId && edge.toId === sel.sourceId)
  );
}

// ── Component ────────────────────────────────────────────────────────

export function ConnectionLines({
  nodes,
  positions,
  hoveredNodeId,
  selectedNodeId,
  selectedEdge,
  filterPassingIds,
  relationshipFilter,
  zoomScale = 1,
  onEdgeClick,
  onEdgeDoubleClick,
}: ConnectionLinesProps) {
  // Build deduplicated edge lists from node.edges
  const edges = useMemo(() => buildEdges(nodes), [nodes]);

  const activeId = hoveredNodeId ?? selectedNodeId;
  const labelsByZoom = zoomScale >= LABEL_ZOOM;

  return (
    <g>
      {edges.map((edge) => {
        // Filter by relationship type when filter is active
        if (relationshipFilter.length > 0) {
          const category: RelationshipType = edge.type === 'parent-child' ? 'parent-child' : 'related';
          if (!relationshipFilter.includes(category)) return null;
        }

        const from = positions.get(edge.fromId);
        const to = positions.get(edge.toId);
        if (!from || !to) return null;

        // Ghost connections to/from filtered-out nodes
        const fromVisible = filterPassingIds.has(edge.fromId);
        const toVisible = filterPassingIds.has(edge.toId);
        if (!fromVisible && !toVisible) return null;
        const isGhosted = !fromVisible || !toVisible;

        const isActive =
          activeId != null &&
          (edge.fromId === activeId || edge.toId === activeId);

        const isSelected = isEdgeSelected(edge, selectedEdge);

        // Dash pattern from data model lineStyle
        const edgeDash = edge.lineStyle === 'dashed' ? '4 3'
          : edge.lineStyle === 'dotted' ? '2 2'
          : undefined;

        let stroke: string;
        let strokeWidth: number;
        let opacity: number;
        let dashArray: string | undefined;

        if (isSelected) {
          stroke = SELECTED_EDGE_COLOR;
          strokeWidth = SELECTED_EDGE_WIDTH;
          opacity = SELECTED_EDGE_OPACITY;
          dashArray = edgeDash;
        } else if (isGhosted) {
          stroke = edge.lineColor || DIM_COLOR;
          strokeWidth = DIM_STROKE_WIDTH;
          opacity = GHOST_OPACITY;
          dashArray = edgeDash;
        } else if (isActive) {
          // D2a: keep the line's own colour and dash; highlight = width + opacity + glow
          stroke = edge.lineColor || DEFAULT_LINE_COLORS[edge.type];
          strokeWidth = HIGHLIGHT_STROKE_WIDTH;
          opacity = HIGHLIGHT_OPACITY;
          dashArray = edgeDash;
        } else {
          stroke = edge.lineColor || DIM_COLOR;
          strokeWidth = DIM_STROKE_WIDTH;
          opacity = activeId ? DIM_OPACITY * 0.4 : DIM_OPACITY;
          dashArray = edgeDash;
        }

        const { cx, cy } = controlPoint(from.x, from.y, to.x, to.y);
        const d = `M ${from.x},${from.y} Q ${cx},${cy} ${to.x},${to.y}`;

        // Highlighted or selected edges get a clickable hit area
        const isClickable = isActive || isSelected;

        const showLabel = edge.label != null && !isGhosted && (isActive || isSelected || labelsByZoom);
        const labelText = edge.label && edge.label.length > LABEL_MAX_CHARS
          ? `${edge.label.slice(0, LABEL_MAX_CHARS - 1)}…`
          : edge.label;
        const mid = showLabel ? curveMidpoint(from, to, cx, cy) : null;

        return (
          <g key={edge.key}>
            {/* Glow layer for highlighted/selected edges */}
            {(isActive || isSelected) && !isGhosted && (
              <path
                d={d}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth * 3}
                strokeDasharray={dashArray}
                opacity={isSelected ? 0.25 : 0.15}
                style={{ pointerEvents: 'none' }}
              />
            )}
            {/* Invisible hit area for clicking */}
            {isClickable && !isGhosted && (
              <path
                data-edge={`${edge.fromId}|${edge.toId}`}
                d={d}
                fill="none"
                stroke="transparent"
                strokeWidth={HIT_AREA_WIDTH}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdgeClick({ sourceId: edge.fromId, targetId: edge.toId });
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onEdgeDoubleClick({ sourceId: edge.fromId, targetId: edge.toId });
                }}
              />
            )}
            <path
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              opacity={opacity}
              style={{ pointerEvents: 'none' }}
            />
            {/* D3a label pill — plain text (no HTML), never a click target */}
            {mid && labelText && (
              <g data-edge-label={edge.key} style={{ pointerEvents: 'none' }}>
                <rect
                  x={mid.x - (labelText.length * LABEL_FONT_SIZE * 0.3 + 5)}
                  y={mid.y - LABEL_FONT_SIZE * 0.85}
                  width={labelText.length * LABEL_FONT_SIZE * 0.6 + 10}
                  height={LABEL_FONT_SIZE * 1.7}
                  rx={LABEL_FONT_SIZE * 0.85}
                  fill="var(--glass-bg-elevated, #1e293b)"
                  stroke={stroke}
                  strokeWidth={0.75}
                  opacity={0.95}
                />
                <text
                  x={mid.x}
                  y={mid.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={LABEL_FONT_SIZE}
                  fill="var(--text-primary, #e2e8f0)"
                >
                  {labelText}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
}
